import React, { FormEvent, useState } from 'react';
import { InputTextField } from '@/components';
import { Button } from 'primereact/button';
import { useContainerExtractedValidations } from '@/hooks';
import classnames from 'classnames';

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import useToastNotification from '@/hooks/useToastNotification';
import { useMutation } from '@tanstack/react-query';
import { verifyRut } from '@/pages/CustomerRegistration/verifyRut';
import { generateANPBooking } from '../generateANPBooking';

import styles from './ANPImport.module.scss';

export interface ANPBookingFormState {
  container: string;
  dispatcherTaxId: string;
}

interface ANPBookingFormErrors {
  container: string;
  dispatcherTaxId: string;
}

const ANPBooking: React.FC = () => {
  const { t } = useTranslation('workOrder');
  const { showToast } = useToastNotification();

  const [formData, setFormData] = useState<ANPBookingFormState>({
    container: '',
    dispatcherTaxId: '',
  });

  const [formErrors, setFormErrors] = useState<ANPBookingFormErrors>({
    container: '',
    dispatcherTaxId: '',
  });

  const { containerIsValid } = useContainerExtractedValidations(
    formData.container,
    true
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const sanitizedValue =
      name === 'container' ? value.replace(/\//g, '') : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: sanitizedValue,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: ANPBookingFormErrors = { ...formErrors };
    let isValid = true;

    if (!formData.container || !containerIsValid) {
      errors = {
        ...errors,
        container:
          formData.container && !containerIsValid
            ? t('bookingInvalid')
            : t('containerRequired'),
      };
      isValid = false;
    }
    const dispatcherTaxIdRegex = /^\d{12}$/;
    if (!formData.dispatcherTaxId) {
      errors.dispatcherTaxId = t('dispatcherTaxIdRequired');
      isValid = false;
    } else if (!dispatcherTaxIdRegex.test(formData.dispatcherTaxId)) {
      errors.dispatcherTaxId = t('dispatcherTaxIdInvalid');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const verifyRutMutation = useMutation({
    mutationFn: verifyRut,
    onError: (error) => {
      showToast({ severity: 'error', detail: `${error}` });
    },
  });

  const generateANPBookingMutation = useMutation({
    mutationFn: generateANPBooking,
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('eirFormValidations.unexpectedError'),
        });
      }
    },
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        dispatcherTaxId: formData.dispatcherTaxId,
        booking: formData.container,
      };

      try {
        const rutData = await verifyRutMutation.mutateAsync({
          rut: payload.dispatcherTaxId,
        });

        if (rutData?.m_Item1 === null && rutData?.m_Item2) {
          showToast({
            severity: 'error',
            detail: rutData?.m_Item2,
          });
        } else {
          generateANPBookingMutation.mutate(payload);
        }
      } catch (error) {
        /* empty */
      }
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className="flex-column md:flex-row flex align-items-start md:align-items-center pl-0 ">
          <InputTextField
            className={`col-12 md:col-5 pl-0 ${styles.inputText}`}
            label={t('booking')}
            id="container"
            onChange={handleInputChange}
            value={formData.container}
            name="container"
            error={formErrors.container}
            placeholder={t('containerExample')}
            longErrorMessage
          />

          <InputTextField
            className={`col-12 md:col-5 pl-0 ${styles.inputText}`}
            label={t('dispatcherTaxId')}
            id="dispatcherTaxId"
            onChange={handleInputChange}
            value={formData.dispatcherTaxId}
            name="dispatcherTaxId"
            error={formErrors.dispatcherTaxId}
            placeholder={t('dispatcherTaxIdPlaceholder')}
          />

          <Button
            label={t('getOrderButton')}
            type="submit"
            className={classnames(styles.buttonOrder)}
            disabled={
              verifyRutMutation.isPending ||
              generateANPBookingMutation.isPending
            }
            loading={
              verifyRutMutation.isPending ||
              generateANPBookingMutation.isPending
            }
          />
        </div>
      </form>
      {generateANPBookingMutation?.data && (
        <div
          className={classnames(
            styles.orderConfirmation,
            generateANPBookingMutation?.data?.value[0]?.ok === false ||
              (!generateANPBookingMutation?.data?.value?.length &&
                styles.errorBg)
          )}
        >
          <FontAwesomeIcon
            icon={
              generateANPBookingMutation?.data?.value[0]?.ok ? faCheck : faTimes
            }
            size="xl"
            className={styles.iconCheck}
          />
          <div>
            {generateANPBookingMutation?.data?.value?.length ? (
              generateANPBookingMutation?.data?.value?.map((val) => (
                <div key={val.contenedor}>
                  <strong>{`${val.mensaje} - ${val.contenedor}`}</strong>
                </div>
              ))
            ) : (
              <strong>{t('workOrderForBooking')}</strong>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
export default ANPBooking;
